import { useState, useRef, useCallback, useEffect } from 'react'
import { showSuccess } from '@/components/shared/Toast'
import Modal from '@/components/shared/Modal'
import { copyToClipboard } from '@/utils'
import { useTranslation } from 'react-i18next'

interface FloatingInviteSpriteProps {
  inviteCode: string
}

const STORAGE_KEY = 'invite_sprite_pos'

function getDefaultPos() {
  const maxW = typeof window !== 'undefined' ? Math.min(window.innerWidth, 430) : 430
  const midY = typeof window !== 'undefined' ? Math.round(window.innerHeight / 2) : 400
  return { x: maxW - 80, y: midY }
}

function loadPos() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const p = JSON.parse(raw) as { x: number; y: number }
      if (p.x >= 0 && p.y >= 0) return p
    }
  } catch {}
  return getDefaultPos()
}

function clamp(x: number, y: number) {
  const maxW = Math.min(window.innerWidth, 430)
  return {
    x: Math.max(0, Math.min(x, maxW - 80)),
    y: Math.max(60, Math.min(y, window.innerHeight - 90)),
  }
}

export default function FloatingInviteSprite({ inviteCode }: FloatingInviteSpriteProps) {
  const { t } = useTranslation()
  const [pos, setPos] = useState(loadPos)
  const [isDragging, setIsDragging] = useState(false)
  const [isSnapping, setIsSnapping] = useState(false)
  const [fallbackText, setFallbackText] = useState('')

  // 用 ref 存拖拽状态，避免闭包捕获旧值
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0, moved: false, lastX: 0, lastY: 0, touchHandled: false })
  const posRef = useRef(pos)
  posRef.current = pos

  const elRef = useRef<HTMLDivElement>(null)

  // 防抖保存位置
  useEffect(() => {
    if (isDragging) return
    const timer = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos))
    }, 500)
    return () => clearTimeout(timer)
  }, [pos, isDragging])

  const handleCopy = useCallback(async () => {
    const link = `${window.location.origin}/?ref=${inviteCode}`
    const ok = await copyToClipboard(link)
    if (ok) {
      showSuccess(t('home.inviteCopied'))
    } else {
      setFallbackText(link)
    }
  }, [inviteCode])

  const snapToEdge = useCallback((x: number, y: number) => {
    const maxW = Math.min(window.innerWidth, 430)
    const snapX = x + 40 < maxW / 2 ? 4 : maxW - 84
    const clamped = clamp(snapX, y)
    setIsSnapping(true)
    setPos(clamped)
    setTimeout(() => setIsSnapping(false), 300)
  }, [])

  // 注册原生 touch 事件（只注册一次，用 ref 读取最新值）
  useEffect(() => {
    const el = elRef.current
    if (!el) return

    const onTouchStart = (e: TouchEvent) => {
      e.stopPropagation()
      const touch = e.touches[0]
      const cur = posRef.current
      dragRef.current = { startX: touch.clientX, startY: touch.clientY, startPosX: cur.x, startPosY: cur.y, moved: false, lastX: cur.x, lastY: cur.y, touchHandled: false }
      setIsDragging(true)
      setIsSnapping(false)
    }

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const touch = e.touches[0]
      const dx = touch.clientX - dragRef.current.startX
      const dy = touch.clientY - dragRef.current.startY
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true
      const newPos = clamp(dragRef.current.startPosX + dx, dragRef.current.startPosY + dy)
      dragRef.current.lastX = newPos.x
      dragRef.current.lastY = newPos.y
      setPos(newPos)
    }

    const onTouchEnd = (e: TouchEvent) => {
      e.stopPropagation()
      e.preventDefault() // 阻止后续 click 事件触发
      setIsDragging(false)
      dragRef.current.touchHandled = true
      // 300ms 后重置，避免误拦正常点击
      setTimeout(() => { dragRef.current.touchHandled = false }, 300)
      if (!dragRef.current.moved) {
        handleCopy()
      } else {
        snapToEdge(dragRef.current.lastX, dragRef.current.lastY)
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: false })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: false })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [handleCopy, snapToEdge]) // 不依赖 pos，通过 posRef 读取

  // 桌面鼠标拖拽
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    // 触摸设备上 touchEnd 已处理，跳过后续的鼠标事件
    if (dragRef.current.touchHandled) return
    e.preventDefault()
    const cur = posRef.current
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: cur.x, startPosY: cur.y, moved: false, lastX: cur.x, lastY: cur.y, touchHandled: false }
    setIsDragging(true)
    setIsSnapping(false)

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - dragRef.current.startX
      const dy = ev.clientY - dragRef.current.startY
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true
      const newPos = clamp(dragRef.current.startPosX + dx, dragRef.current.startPosY + dy)
      dragRef.current.lastX = newPos.x
      dragRef.current.lastY = newPos.y
      setPos(newPos)
    }
    const onMouseUp = () => {
      setIsDragging(false)
      if (!dragRef.current.moved) {
        handleCopy()
      } else {
        const cur = posRef.current
        snapToEdge(cur.x, cur.y)
      }
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }, [handleCopy, snapToEdge])

  return (
    <>
      <div
        ref={elRef}
        onMouseDown={onMouseDown}
        className="fixed z-40 select-none"
        style={{
          left: pos.x,
          top: pos.y,
          touchAction: 'none',
          transition: isSnapping ? 'left 0.3s ease, top 0.3s ease' : 'none',
          animation: isDragging ? 'none' : 'spriteIdle 3s ease-in-out infinite',
          willChange: 'left, top',
        }}
      >
        <div
          className="flex items-center gap-1.5 rounded-full cursor-pointer"
          style={{
            padding: '8px 14px 8px 10px',
            background: 'linear-gradient(135deg, #2E6BFF, #1E4ACC)',
            border: '2px solid rgba(91,140,255,0.4)',
            boxShadow: '0 0 20px rgba(46,107,255,0.3), 0 4px 12px rgba(0,0,0,0.3)',
            animation: isDragging ? 'none' : 'pulseGlow 3s ease-in-out infinite',
          }}
        >
          <span className="material-icons" style={{ color: '#FFFFFF', fontSize: 18, pointerEvents: 'none' }}>
            people
          </span>
          <span style={{
            color: '#FFFFFF',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.02em',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}>
            {t('home.invite')}
          </span>
        </div>
      </div>

      <Modal
        open={!!fallbackText}
        onClose={() => setFallbackText('')}
        title={t('home.copyFailedTitle')}
        description={t('home.copyFailedDesc')}
        size="sm"
      >
        <div
          className="w-full p-3 rounded-lg text-xs break-all"
          style={{
            background: '#1A1A38',
            border: '1px solid #3B2A70',
            color: '#5B8CFF',
            userSelect: 'all',
            WebkitUserSelect: 'all',
          }}
        >
          {fallbackText}
        </div>
      </Modal>
    </>
  )
}
